import React, { memo, useMemo, useCallback, useState, useEffect } from "react";
import ModernQuestionnaireFormSection from "./ModernQuestionnaireFormSection";

const ModernQuestionnaireSection = memo(
  ({
    section,
    formData,
    onFieldChange,
    onMultiSelect,
    validationErrors = {},
    isActive = false,
    isCompleted = false,
    isCollapsible = false,
    sectionIndex = 0,
    totalSections = 0,
    basicInfoLockedFromTrip = false,
  }) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [fieldErrors, setFieldErrors] = useState({});

    // Helper function to get nested values from form data
    const getNestedValue = useCallback((data, fieldId) => {
      if (!data || !fieldId) return null;

      // Handle nested field IDs like "basicInformation.fullName"
      if (fieldId.includes(".")) {
        const parts = fieldId.split(".");
        let value = data;
        for (const part of parts) {
          value = value?.[part];
          if (value === undefined) return null;
        }
        return value;
      }

      // Map field IDs to their sections for nested access
      const fieldToSection = {
        // Basic Information
        fullName: "basicInformation",
        email: "basicInformation",
        phoneNumber: "basicInformation",
        startDate: "basicInformation",
        endDate: "basicInformation",

        // Group Information
        adults: "groupInfo",
        numAdultMales: "groupInfo",
        numAdultFemales: "groupInfo",
        kids: "groupInfo",
        numBoys: "groupInfo",
        numGirls: "groupInfo",
        agesOfBoys: "groupInfo",
        agesOfGirls: "groupInfo",

        // Ticket & Park Access
        ticketType: "parkTicketAccess",
        hasParkHopper: "parkTicketAccess",

        // Park Selection
        parks: "parkVisitPlans",
        mkDays: "parkVisitPlans",
        hsDays: "parkVisitPlans",
        epcotDays: "parkVisitPlans",
        akDays: "parkVisitPlans",
        tlDays: "parkVisitPlans",
        bbDays: "parkVisitPlans",

        // Accommodation & Transportation
        transportation: "accommodation",
        stayingOnProperty: "accommodation",
        resort: "accommodation",
        otherHotel: "accommodation",

        // Attraction & Ride Preferences
        lightningLane: "attractionRideStrategy",
        rideStrategy: "attractionRideStrategy",
        mustRides: "attractionRideStrategy",

        // Shows & Firework Preferences
        showStrategy: "showFireworkStrategy",
        mustSeeShows: "showFireworkStrategy",
        fantasmicDiningPackageInterest: "showFireworkStrategy",
        fantasmicDiningPackageRestaurants: "showFireworkStrategy",

        // Character Meet & Greets
        interestedInCharacterMeetGreets: "characterMeetGreets",
        mustMeetCharacters: "characterMeetGreets",
        mustMeetCharactersOther: "characterMeetGreets",

        // Dining Preferences
        diningPlanInterest: "diningPreferences",
        restaurantStrategy: "diningPreferences",
        mustTryRestaurants: "diningPreferences",
        diningTime: "diningPreferences",
        allergies: "diningPreferences",


        // Time & Energy Preferences
        parkStartTime: "timeEnergy",
        breakTime: "timeEnergy",

        // Special Considerations
        celebration: "specialConsiderations",
        celebrationDetails: "specialConsiderations",
        celebrationDate: "specialConsiderations",
        celebrationOtherDetails: "specialConsiderations",
        hasDisability: "specialConsiderations",
        disabilities: "specialConsiderations",
        additionalInfo: "specialConsiderations",
        additionalInfoDetails: "specialConsiderations",
      };

      const section = fieldToSection[fieldId];
      if (section && data[section]) {
        return data[section][fieldId];
      }

      return data[fieldId];
    }, []);

    // Memoize section validation to prevent unnecessary recalculations
    const sectionValidation = useMemo(() => {
      if (!section?.fields || !formData || !Array.isArray(section.fields)) {
        return {
          isValid: true,
          errors: [],
        };
      }

      const errors = [];
      let isValid = true;

      // Only validate required fields if they have been interacted with
      // This prevents showing errors immediately when navigating to a section
      section.fields.forEach((field) => {
        const fieldValue = getNestedValue(formData, field.id);
        const isFieldCompleted =
          fieldValue !== undefined && fieldValue !== null && fieldValue !== "";
        
        // Only show validation errors for required fields that have been touched
        // This is handled by the main questionnaire validation on Next button click
        // Don't show errors here to prevent immediate validation on section load
      });

      return {
        isValid,
        errors,
      };
    }, [section?.fields, formData, getNestedValue]);

    // Memoize field change handler to prevent unnecessary re-renders
    const handleFieldChange = useCallback(
      (fieldId, value) => {
        onFieldChange(fieldId, value);

        // Clear field-specific errors when user starts typing
        setFieldErrors((prev) => {
          if (prev[fieldId]) {
            const newErrors = { ...prev };
            delete newErrors[fieldId];
            return newErrors;
          }
          return prev;
        });

        // Only validate required fields if the user has interacted with them
        // Don't show errors immediately for empty required fields
      },
      [onFieldChange, section?.fields]
    );

    // Handle multi-select changes
    const handleMultiSelect = useCallback(
      (fieldId, selectedItems) => {
        if (onMultiSelect) {
          onMultiSelect(fieldId, selectedItems);
        }
      },
      [onMultiSelect]
    );

    // Get section status color
    const getStatusColor = useCallback(() => {
      if (isCompleted) return "text-green-400 border-green-200";
      if (isActive) return "text-gray-100 border-white/20";
      return "text-gray-400 border-white/20";
    }, [isCompleted, isActive]);

    // Handle section expansion/collapse
    const handleToggleExpansion = useCallback(() => {
      if (isCollapsible) {
        setIsExpanded((prev) => !prev);
      }
    }, [isCollapsible]);

    return (
      <div className={`questionnaire-section ${getStatusColor()}`}>
        {/* Section Header with Progress and Controls */}
        <div className="mb-1">
          {/* Section Title and Status - Removed */}
          <div className="flex justify-between items-center mb-1">
            {/* Collapse/Expand Button */}
            {isCollapsible && (
              <button
                onClick={handleToggleExpansion}
                className="p-1 text-gray-400 transition-colors hover:text-gray-200"
                aria-label={isExpanded ? "Collapse section" : "Expand section"}
              >
                {isExpanded ? "−" : "+"}
              </button>
            )}
          </div>
        </div>

        {/* Validation Errors - Only show external validation errors */}
        {Object.keys(fieldErrors).length > 0 && (
          <div className="p-2.5 mb-2 bg-red-50 rounded-lg border border-red-200">
            <div>
              <h4 className="text-sm font-medium text-red-800">
                Please complete the following:
              </h4>
              <ul className="mt-1 text-sm list-disc list-inside text-red-700">
                {Object.entries(fieldErrors).map(([fieldId, error]) => (
                  <li key={fieldId}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* External Validation Errors */}
        {validationErrors && Object.keys(validationErrors).length > 0 && (
          <div className="p-2.5 mb-2 bg-orange-50 rounded-lg border border-orange-200">
            <div>
              <h4 className="text-sm font-medium text-orange-800">
                Additional validation issues:
              </h4>
              <ul className="mt-1 text-sm list-disc list-inside text-orange-700">
                {Object.entries(validationErrors).map(([fieldId, error]) => (
                  <li key={fieldId}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Section Content */}
        {(!isCollapsible || isExpanded) && (
          <ModernQuestionnaireFormSection
            currentSection={section}
            formData={formData}
            handleChange={handleFieldChange}
            handleMultiSelect={handleMultiSelect}
            basicInfoLockedFromTrip={basicInfoLockedFromTrip}
          />
        )}
      </div>
    );
  }
);

ModernQuestionnaireSection.displayName = "ModernQuestionnaireSection";

export default ModernQuestionnaireSection;